// @flow

import { completeRequest, beginRequest } from "@store/utils";
import type { AbsenceReasonsState } from "./types";
import type { Action } from "redux-action";
import type { ApprovableContainer } from "@modules/service/types";
import type { AbsenceReason } from "@modules/service/reducer/absence-reasons/types";

const startRequest = beginRequest(["requestsInProgress"]);

const finishRequest = completeRequest(["requestsInProgress"]);

export const onGetAbsenceReasonsList = (state: AbsenceReasonsState) => () => startRequest(state);

export const onAddNewAbsenceReason = (state: AbsenceReasonsState) => () => startRequest(state);
export const onAddNewAbsenceReasonSuccess = (state: AbsenceReasonsState) => () =>
  finishRequest(state);
export const onAddNewAbsenceReasonError = (state: AbsenceReasonsState) => () =>
  finishRequest(state);

export const onExportLiveAbsenceReasons = (state: AbsenceReasonsState) => () => startRequest(state);
export const onExportLiveAbsenceReasonsSuccess = (state: AbsenceReasonsState) => () =>
  finishRequest(state);
export const onExportLiveAbsenceReasonsError = (state: AbsenceReasonsState) => () =>
  finishRequest(state);

export const onUpdateAbsenceReason = (state: AbsenceReasonsState) => () => startRequest(state);
export const onUpdateAbsenceReasonSuccess = (state: AbsenceReasonsState) => () =>
  finishRequest(state);
export const onUpdateAbsenceReasonError = (state: AbsenceReasonsState) => () =>
  finishRequest(state);

export const onGetAbsenceReasonsListSuccess = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  list: payload,
});

export const onGetAbsenceReasonsListError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onGetAbsenceReasonsActiveList = (state: AbsenceReasonsState) => () =>
  startRequest(state);

export const onGetAbsenceReasonsActiveListSuccess = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  activeList: payload,
});

export const onGetAbsenceReasonsActiveListError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onGetAbsenceReasonsDraftList = (state: AbsenceReasonsState) => () =>
  startRequest(state);

export const onGetAbsenceReasonsDraftListSuccess = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  draftList: payload,
});

export const onGetAbsenceReasonsDraftListError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onGetAbsenceReasonsPendingList = (state: AbsenceReasonsState) => () =>
  startRequest(state);

export const onGetAbsenceReasonsPendingListSuccess = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  pendingList: payload,
});

export const onGetAbsenceReasonsPendingListError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onNewAbsenceReason = (state: AbsenceReasonsState) => (action: Action<void>) => ({
  ...state,
  draft: {
    name: null,
    displayName: null,
    reasonType: null,
    nurseTransferEligibility: null,
    parentReason: null,
  },
});

export type ShowAbsenceReasonModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

export const onShowAddAbsenceReasonModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showAddAbsenceReasonModal: payload,
  },
});

export const onUpdateAbsenceReasonDraft = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  draft: payload,
});

export const onSetAbsenceReasonRevisionSchema = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  error: null,
  schema: payload,
});

export const onGetAbsenceReasonActiveSuccess = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetAbsenceReasonActiveError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onSetAbsenceReasonStatus = (state: AbsenceReasonsState) => ({ payload }: Object) => ({
  ...state,
  configuration: {
    ...state.configuration,
    status: payload,
  },
});

export const onGetAbsenceReason = (state: AbsenceReasonsState) => () => startRequest(state);

export const onGetAbsenceReasonSuccess = (state: AbsenceReasonsState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetAbsenceReasonError = (state: AbsenceReasonsState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowAbsenceReasonEditModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditModal: payload,
  },
});

export const onSetShowSubmitAbsenceReasonModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showSubmitModal: payload,
  },
});

export const onSetShowCancelAbsenceReasonModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelModal: payload,
  },
});

export const onSubmitAbsenceReasonForReviewError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelAbsenceReasonReviewError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onStartAbsenceReasonDraftSuccess = (state: AbsenceReasonsState) => (
  action: Action<ApprovableContainer<AbsenceReason>>,
) => ({
  ...state,
  draft: {
    ...((action.payload && (action.payload.active || action.payload.draft)) || {}),
  },
});

export const onSetShowDiscardAbsenceReasonModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDiscardModal: payload,
  },
});

export const onSetShowCancelDiscardAbsenceReasonModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelDiscardModal: payload,
  },
});

export const onDuplicateAbsenceReasonSuccess = (state: AbsenceReasonsState) => (
  action: Action<AbsenceReason>,
) => ({
  ...state,
  draft: {
    name: null,
    displayName: null,
    reasonType: (action.payload || {}).reasonType,
    parentReason: (action.payload || {}).parentReason,
    nurseTransferEligibility: (action.payload || {}).nurseTransferEligibility,
    isActive: (action.payload || {}).isActive,
  },
});

export const onSetShowDisableAbsenceReasonModal = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDisableModal: payload,
  },
});

export const onStartAbsenceReasonDraftError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDiscardAbsenceReasonDraftError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDuplicateAbsenceReasonError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelAbsenceReasonReviewAndDiscardError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDisableAbsenceReasonError = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onChangeAbsenceReasonsFilterState = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  filterState: payload,
});

export const onChangeAbsenceReasonsFilterType = (state: AbsenceReasonsState) => ({
  payload,
}: Object) => ({
  ...state,
  filterType: payload,
});
